import {
  Row,
  Col,
  Card,
  Badge,
  Divider,
  Spin,
  Tag,
  Alert,
} from "antd";
import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import languages from "../../config/language";
import { ArrowLeftIcon, CheckCircle2, XCircle } from "lucide-react";

// Human-readable restriction label for an enabled feature object
const restrictionLabel = (f) => {
  if (!f) return "";
  switch (f.access) {
    case "count": return `${f.count || 0} times`;
    case "once": return "Only once";
    case "days": return `${f.days || 0} days`;
    case "full":
    default: return "Full access";
  }
};

const FEATURE_CONFIG = [
  {
    category: "Product Search",
    features: [
      { key: "product_search", label: "Simple & Advance Search" },
    ]
  },
  {
    category: "Price List",
    features: [
      { key: "pricelist_email_download", label: "Email & Download" },
      { key: "pricelist_inapp_view", label: "In-app View" },
      { key: "price_change_notification", label: "Price Change Notification" },
    ]
  },
  {
    category: "New Product Launch",
    features: [
      { key: "new_product_launch", label: "New Product Launch" },
    ]
  },
  {
    category: "Catalogue",
    features: [
      { key: "catalogue_email_download", label: "Email & Download" },
      { key: "catalogue_inapp_view", label: "In-app View" },
      { key: "catalogue_release_notification", label: "New Catalogue Release Notification" },
    ]
  },
  {
    category: "Schemes",
    features: [
      { key: "schemes_inapp_view", label: "In-app View" },
      { key: "schemes_notification", label: "Notification" },
    ]
  },
  {
    category: "Brands",
    features: [
      { key: "brands_list", label: "List of Brands" },
      { key: "brands_details", label: "Details" },
      { key: "brand_notification", label: "New Brand Notification" },
    ]
  },
  {
    category: "Order Placements",
    features: [
      { key: "order_placements", label: "Order Placements" },
    ]
  },
  {
    category: "Coupon Redemption",
    features: [
      { key: "coupon_redemption", label: "Coupon Redemption" },
    ]
  },
  {
    category: "Warranty For Batteries",
    features: [
      { key: "warranty_batteries", label: "Warranty For Batteries" },
    ]
  },
];

function View() {

  const sectionName   =   "Subscription";
  const routeName     =   "subscription";

  const location      =   useLocation();
  const params        =   useParams();
  const { request }   =   useRequest();

  const [list, setList]       = useState({});
  const [loading, setLoading] = useState(false);
  const [trialDays, setTrialDays] = useState(null);

  const fetchSettings = () => {
    request({
      url: apiPath.commonSetting,
      method: "GET",
      onSuccess: (data) => {
        if (data.data) {
          setTrialDays(data.data.free_trial);
        }
      },
    });
  };

  const fetchData = (id) => {
    setLoading(true);
    request({
      url: apiPath.viewSubscription + "/" + id,
      method: "GET",
      onSuccess: (data) => {
        setList(data.data);
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchSettings();
    fetchData(params.id);
  }, [params.id]);

  return (
    <Row gutter={[16, 16]}>

      <Col xs={24} lg={12}>
        <Card title={`${sectionName} Details`}>
          <Spin spinning={loading} tip="Loading...">
            <div className="view-main-list">

              <div className="view-inner-cls">
                <h5>Type:</h5>
                <h6>
                  {list.type === "paid" ? <Tag color="blue">Paid</Tag>
                    : list.type === "trial" ? <Tag color="orange">Trial</Tag>
                    : "-"}
                </h6>
              </div>

              {list.type === "trial" && (
                <Alert
                  type="info"
                  showIcon
                  style={{ margin: "8px 0 12px" }}
                  message="Trial subscription"
                  description={<span>Newly registered users automatically get a <b>{trialDays || "—"}</b>-day trial period (managed in Settings) with the features below. When the trial ends, the user is switched to <b>Free</b> unless they purchase a paid subscription before the trial ends.</span>}
                />
              )}

              <div className="view-inner-cls">
                <h5>Status:</h5>
                <h6>
                  {list.is_active ? (
                    <Badge status="success" text="Active" />
                  ) : (
                    <Badge status="error" text="Inactive" />
                  )}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Created On:</h5>
                <h6>
                  {list.created_at
                    ? moment(list.created_at).format("DD-MMM-YYYY HH:mm")
                    : "-"}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Last Updated On:</h5>
                <h6>
                  {list.updated_at
                    ? moment(list.updated_at).format("DD-MMM-YYYY HH:mm")
                    : "-"}
                </h6>
              </div>

              <div className="float-right mt-3">
                <Link
                  className="ant-btn ant-btn-secondary"
                  to={`/${routeName}${location.search}`}
                >
                  <ArrowLeftIcon size={15} /> Back
                </Link>
              </div>
            </div>
          </Spin>
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card title="Associations">
          <Spin spinning={loading} tip="Loading...">

            <div className="view-inner-cls">
              <h5>Segment:</h5>
              <h6>{list.segment_id?.en_name || "-"}</h6>
            </div>

          </Spin>
        </Card>
      </Col>

      {list.type === "paid" && list.pricing && (
        <Col xs={24}>
          <Card title="Pricing Plans">
            <Spin spinning={loading} tip="Loading...">
              <table style={{ width: '100%', borderCollapse: 'collapse', maxWidth: 600 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', borderBottom: '1px solid #e8e8e8' }}>Duration</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', borderBottom: '1px solid #e8e8e8' }}>Price (₹)</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', borderBottom: '1px solid #e8e8e8' }}>Savings (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "1 Month", key: "price_1_month", months: 1 },
                    { label: "3 Months", key: "price_3_month", months: 3 },
                    { label: "6 Months", key: "price_6_month", months: 6 },
                    { label: "12 Months", key: "price_12_month", months: 12 },
                  ].map((tier) => {
                    const price = list.pricing?.[tier.key] || 0;
                    const monthlyPrice = list.pricing?.price_1_month || 0;
                    const savings = (monthlyPrice * tier.months) - price;
                    return (
                      <tr key={tier.key}>
                        <td style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0' }}>{tier.label}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>
                          ₹{price.toLocaleString('en-IN')}
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', borderBottom: '1px solid #f0f0f0', color: savings > 0 ? '#52c41a' : '#999' }}>
                          {savings > 0 ? `₹${savings.toLocaleString('en-IN')}` : '₹0'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Spin>
          </Card>
        </Col>
      )}

      <Col xs={24}>
        <Card title="Language Data">
          <Spin spinning={loading} tip="Loading...">
            {languages.map((lang) => (
              <div key={lang.short} className="mb-3">
                <h4>{lang.long}</h4>

                <div className="view-inner-cls">
                  <h5>Name:</h5>
                  <h6>{list[`${lang.short}_name`] || "-"}</h6>
                </div>

                <div className="view-inner-cls">
                  <h5>Description:</h5>
                  <h6
                    className="lang-description"
                    dangerouslySetInnerHTML={{
                      __html: list[`${lang.short}_description`] || "-",
                    }}
                  />
                </div>

                <Divider />
              </div>
            ))}
          </Spin>
        </Card>
      </Col>

      <Col xs={24}>
        <Card title="Subscription Features">
          <Spin spinning={loading} tip="Loading...">
            <Row gutter={[24, 0]}>
              {FEATURE_CONFIG.map((group) => (
                <Col span={24} key={group.category}>
                  <div style={{ background: '#f5f5f5', padding: '8px 16px', borderRadius: 6, marginBottom: 8 }}>
                    <strong>{group.category}</strong>
                  </div>
                  <Row gutter={[24, 4]} style={{ paddingLeft: 16, marginBottom: 12 }}>
                    {group.features.map((feature) => {
                      const f = list.features?.[feature.key];
                      const enabled = !!(f && f.enabled);
                      return (
                        <Col xs={24} sm={12} md={8} key={feature.key}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                            {enabled ? (
                              <CheckCircle2 size={18} color="#52c41a" />
                            ) : (
                              <XCircle size={18} color="#ff4d4f" />
                            )}
                            <span>{feature.label}</span>
                            {enabled && (
                              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#888' }}>
                                {restrictionLabel(f)}
                              </span>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </Col>
              ))}
            </Row>
          </Spin>
        </Card>
      </Col>
    </Row>
  );
}

export default View;
